import {
  Component,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { DetailInfo } from 'common/model';
import { displayValueForField, generateFormGroupForDetailInfo } from 'shared/shared.helper';

// One grid column: a header title + the 1..n fields STACKED inside each cell of that column.
// Form scale (Action Plan): a column = a template form GROUP (group title = header, the group's
// fields = the cell stack). Field scale (RepeatableTable field): a column = one sub-field.
export interface IRepeatableGridColumn {
  title: string;
  fields: DetailInfo[];
}

// The system completion stamp for one row (Action Plan completion column), keyed by row guid.
export interface IRepeatableGridRowStamp {
  completed: boolean;
  completedDate?: string | null;
  completedByName?: string | null;
}

/**
 * Shared repeatable-grid renderer used at BOTH scales:
 *  - form scale   (the Action Plan: columns = the template's groups, cell = stacked fields)
 *  - field scale  (a RepeatableTable field: columns = the field's single sub-columns)
 *
 * The host owns the `rows` FormArray (each entry is a FormGroup keyed by field
 * evideInputContentGuid, flat across all columns). This component only renders it and drives
 * add/remove. Each cell field re-enters app-detail-info-type-container, so every field type is
 * supported per cell for free. Per-row column/field DetailInfo CLONES (rowViews) isolate rows from
 * one another (mirrors the attendance grid), so a leaf control's per-row state never bleeds
 * across rows.
 *
 * Row identity (form scale): with `trackRowIdentity` every row FormGroup carries a hidden
 * `__rowGuid` control - the host seeds saved rows with their guid, addRow starts a new row with
 * an empty one, and the host sends it back so the server can keep per-row state (completion
 * stamps, session links). `completionColumnGuid` names the column whose checkbox / toggle marks a
 * row completed; the saved stamp for that row (`rowStamps`) is shown read-only beside the cell.
 *
 * viewOnly renders the grid as clean read-only OUTPUT (resolved display text per field), not a
 * disabled form.
 */
@Component({
  selector: 'app-detail-info-repeatable-table',
  templateUrl: './detail-info-repeatable-table.component.html',
  standalone: false
})
export class DetailInfoRepeatableTableComponent implements OnChanges {
  @Input() columns: IRepeatableGridColumn[] = [];
  @Input() rows!: FormArray;
  @Input() viewOnly = false;
  @Input() addRowLabel = 'Add Row';
  @Input() trackRowIdentity = false;
  @Input() completionColumnGuid: string | null = null;
  @Input() rowStamps: { [rowGuid: string]: IRepeatableGridRowStamp } = {};

  // Parallel to rows.controls: cloned column structures per row for isolated rendering.
  public rowViews: IRepeatableGridColumn[][] = [];

  constructor(private fb: FormBuilder) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rows'] || changes['columns']) {
      this.syncRowViews();
    }
  }

  private get allFields(): DetailInfo[] {
    return this.columns.reduce<DetailInfo[]>((acc, c) => acc.concat(c.fields || []), []);
  }

  private cloneColumns(): IRepeatableGridColumn[] {
    return this.columns.map((c) => ({
      title: c.title,
      fields: (c.fields || []).map((f) => f.clone())
    }));
  }

  private syncRowViews(): void {
    const count = this.rows?.length ?? 0;
    this.rowViews = [];
    for (let i = 0; i < count; i++) {
      this.rowViews.push(this.cloneColumns());
    }
  }

  rowGroup(index: number): FormGroup {
    return this.rows.at(index) as FormGroup;
  }

  hasMandatory(col: IRepeatableGridColumn): boolean {
    return (col.fields || []).some((f) => f.isMandatory);
  }

  // In-cell field labels: only when a column stacks several fields, and the field's own title
  // adds information (non-blank + not just repeating the column header). Blank titles are the
  // authoring hook to suppress a label deliberately.
  showFieldLabel(col: IRepeatableGridColumn, field: DetailInfo): boolean {
    if ((col.fields?.length ?? 0) <= 1) {
      return false;
    }
    const title = (field.title || '').trim();
    if (!title) {
      return false;
    }
    return title.toLowerCase() !== (col.title || '').trim().toLowerCase();
  }

  // Read-only output: resolve the control value to display text (option labels, formatted
  // dates, Yes/No toggles) instead of showing a disabled control.
  displayFor(field: DetailInfo, rowIndex: number): string {
    const value = this.rowGroup(rowIndex)?.get(field.evideInputContentGuid)?.value;
    return displayValueForField(field, value);
  }

  // The completion column's cell shows the saved stamp ("date, name") under the tick.
  isCompletionField(field: DetailInfo): boolean {
    return !!this.completionColumnGuid && field.evideInputContentGuid === this.completionColumnGuid;
  }

  stampFor(rowIndex: number): IRepeatableGridRowStamp | null {
    const rowGuid = this.rowGroup(rowIndex)?.get('__rowGuid')?.value;
    const stamp = rowGuid ? this.rowStamps?.[rowGuid] : null;
    return stamp && stamp.completed ? stamp : null;
  }

  addRow(): void {
    if (!this.rows || this.viewOnly) {
      return;
    }
    const rowGroup = this.fb.group({});
    if (this.trackRowIdentity) {
      rowGroup.addControl('__rowGuid', this.fb.control(''));
    }
    this.allFields.forEach((field) => generateFormGroupForDetailInfo(rowGroup, field.clone()));
    this.rows.push(rowGroup);
    this.rowViews.push(this.cloneColumns());
  }

  removeRow(index: number): void {
    if (!this.rows || this.viewOnly) {
      return;
    }
    this.rows.removeAt(index);
    this.rowViews.splice(index, 1);
  }
}
